import {
	AI_REVIEW_MAX_BYTES,
	DOCUMENT_CONTENT_TYPES,
	DOCUMENT_MAX_BYTES,
	DOCUMENT_TYPES,
	type DocumentType,
	isAiReviewableContentType,
} from "@zplanem/responses"
import { useEffect, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { ApiError } from "../../../lib/api-client"
import { formatBytes } from "../../../lib/format-bytes"
import {
	type DocumentScope,
	useUploadDocument,
} from "../../hooks/use-documents"
import { getTranslations } from "../../i18n"
import { useLocale } from "../../locale-context"

const ACCEPT = Object.keys(DOCUMENT_CONTENT_TYPES).join(",")

export function DocumentUploadModal({
	propertyId,
	projects,
	fixedScope,
	onClose,
}: {
	propertyId: string
	projects?: { id: string; name: string }[]
	fixedScope?: DocumentScope
	onClose: () => void
}) {
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const td = t.projects.documents
	const [file, setFile] = useState<File | null>(null)
	const [title, setTitle] = useState("")
	// Semantic type, immutable after upload (ZPL-263). Defaults to contract —
	// the common case + the only type that triggers the AI review.
	const [documentType, setDocumentType] = useState<DocumentType>("contract")
	// AI contract review is on by default (ZPL-209) — owners opt out.
	const [aiReadable, setAiReadable] = useState(true)
	const [error, setError] = useState<string | null>(null)
	const [dragging, setDragging] = useState(false)
	const [scopeValue, setScopeValue] = useState<string>("property")
	const fileRef = useRef<HTMLInputElement>(null)

	const selectedScope: DocumentScope =
		fixedScope ??
		(scopeValue === "property"
			? { type: "property", propertyId }
			: { type: "project", propertyId, projectId: scopeValue })

	const upload = useUploadDocument(selectedScope)

	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose()
		}
		window.addEventListener("keydown", handler)
		return () => window.removeEventListener("keydown", handler)
	}, [onClose])

	function onPick(picked: File) {
		if (picked.size > DOCUMENT_MAX_BYTES) {
			setError(td.errorFileTooLarge)
			setFile(null)
			return
		}
		setError(null)
		setFile(picked)
		if (!title.trim()) setTitle(picked.name.replace(/\.[^.]+$/, ""))
	}

	async function submit() {
		if (!file || !title.trim()) return
		setError(null)
		try {
			await upload.mutateAsync({
				file,
				title: title.trim(),
				aiReadable,
				documentType,
			})
			onClose()
		} catch (err) {
			setError(err instanceof ApiError ? err.message : td.errorUpload)
		}
	}

	const pending = upload.isPending
	const submitDisabled = pending || !file || !title.trim()

	// The file uploads either way; this only warns that AI review will be
	// skipped (non-PDF, or over the AI-review size ceiling — below the upload cap).
	const aiReviewNotice = ((): string | null => {
		if (!aiReadable || !file) return null
		if (file.size > AI_REVIEW_MAX_BYTES) {
			return td.aiUploadNoticeTooLarge.replace(
				"{size}",
				formatBytes(AI_REVIEW_MAX_BYTES),
			)
		}
		if (!isAiReviewableContentType(file.type))
			return td.aiUploadNoticeUnsupported
		return null
	})()

	return (
		<div
			role="dialog"
			aria-modal="true"
			aria-label={td.uploadModalTitle}
			className="fixed inset-0 z-50 flex items-center justify-center px-4"
		>
			<button
				type="button"
				aria-label={td.cancel}
				className="absolute inset-0 bg-black/60"
				onClick={onClose}
			/>
			<div className="relative w-full max-w-md rounded-lg border border-border bg-popover text-popover-foreground p-6 shadow-2xl">
				<div className="flex items-start justify-between">
					<h2 className="text-xl font-bold text-foreground">
						{td.uploadModalTitle}
					</h2>
					<button
						type="button"
						onClick={onClose}
						aria-label={td.cancel}
						className="text-muted-foreground hover:text-foreground px-2 py-1"
					>
						×
					</button>
				</div>

				<div className="mt-4 space-y-4">
					<div>
						<label
							htmlFor="doc-file"
							className="block text-sm text-foreground/80 mb-1"
						>
							{td.fileLabel}
						</label>
						<input
							ref={fileRef}
							id="doc-file"
							type="file"
							accept={ACCEPT}
							className="hidden"
							onChange={(e) => {
								const picked = e.currentTarget.files?.[0]
								e.currentTarget.value = ""
								if (picked) onPick(picked)
							}}
						/>
						<button
							type="button"
							onClick={() => fileRef.current?.click()}
							onDragOver={(e) => {
								e.preventDefault()
								setDragging(true)
							}}
							onDragLeave={() => setDragging(false)}
							onDrop={(e) => {
								e.preventDefault()
								setDragging(false)
								const f = e.dataTransfer.files?.[0]
								if (f) onPick(f)
							}}
							className={[
								"w-full cursor-pointer rounded-xl border-2 border-dashed px-4 py-6 flex flex-col items-center justify-center gap-2 text-center transition-colors",
								dragging
									? "border-ring bg-muted/50"
									: "border-border bg-muted/30 hover:bg-muted/50",
							].join(" ")}
						>
							{file ? (
								<span className="max-w-full truncate text-sm font-medium text-foreground">
									{file.name}
								</span>
							) : (
								<span className="text-sm text-foreground/80">
									{td.fileDropHint}
								</span>
							)}
						</button>
					</div>

					<div>
						<label
							htmlFor="doc-title"
							className="block text-sm text-foreground/80 mb-1"
						>
							{td.titleLabel}
						</label>
						<input
							id="doc-title"
							type="text"
							value={title}
							onChange={(e) => setTitle(e.target.value)}
							placeholder={td.titlePlaceholder}
							className="w-full rounded-xl bg-muted border border-border px-4 py-2.5 text-foreground placeholder:text-muted-foreground outline-none focus:border-ring"
						/>
					</div>

					<div>
						<label
							htmlFor="doc-type"
							className="block text-sm text-foreground/80 mb-1"
						>
							{td.documentTypeLabel}
						</label>
						<select
							id="doc-type"
							value={documentType}
							onChange={(e) => setDocumentType(e.target.value as DocumentType)}
							className="w-full rounded-xl bg-muted border border-border px-4 py-2.5 text-foreground outline-none focus:border-ring"
						>
							{DOCUMENT_TYPES.map((dt) => (
								<option key={dt} value={dt}>
									{td.documentTypeOptions[dt]}
								</option>
							))}
						</select>
					</div>

					{!fixedScope ? (
						<div>
							<label
								htmlFor="doc-scope"
								className="block text-sm text-foreground/80 mb-1"
							>
								{td.scopeFieldLabel}
							</label>
							<select
								id="doc-scope"
								value={scopeValue}
								onChange={(e) => setScopeValue(e.target.value)}
								className="w-full rounded-xl bg-muted border border-border px-4 py-2.5 text-foreground outline-none focus:border-ring"
							>
								<option value="property">{td.scopeProperty}</option>
								{(projects ?? []).map((p) => (
									<option key={p.id} value={p.id}>
										{p.name}
									</option>
								))}
							</select>
						</div>
					) : null}

					<div>
						<label className="flex items-center gap-2 cursor-pointer select-none">
							<input
								type="checkbox"
								checked={aiReadable}
								onChange={(e) => setAiReadable(e.target.checked)}
								className="h-4 w-4 rounded border-border"
							/>
							<span className="text-sm text-foreground/80">
								{td.aiReadableLabel}
							</span>
						</label>
						<p className="mt-1 pl-6 text-xs text-muted-foreground">
							{td.aiReadableHint}
						</p>
						{aiReviewNotice ? (
							<p className="mt-1 pl-6 text-xs text-warning">{aiReviewNotice}</p>
						) : null}
					</div>

					{error ? <p className="text-sm text-destructive">{error}</p> : null}
				</div>

				<div className="mt-5 flex justify-end gap-2">
					<Button
						type="button"
						variant="ghost"
						onClick={onClose}
						disabled={pending}
					>
						{td.cancel}
					</Button>
					<Button
						type="button"
						onClick={() => void submit()}
						disabled={submitDisabled}
					>
						{pending ? td.uploading : td.upload}
					</Button>
				</div>
			</div>
		</div>
	)
}
